import { useState } from "react";
import { Trash2, AlertTriangle, X, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useSuperMode } from "../../context/SuperModeContext";

/**
 * V29 — Admin-side "Permanent Delete" of a tutor profile.
 *
 * Renders a Danger Zone card. Clicking the button opens a confirmation modal
 * that requires:
 *   1. Typing "DELETE PERMANENTLY" (defence in depth)
 *   2. The admin to already be in Super Mode (X-Super-Mode-Token sent
 *      automatically by the axios interceptor). If Super Mode is off, the
 *      backend returns 403 super_mode_required and we show a helpful message.
 *
 * Per the spec, hard-delete wipes the tutor application + account + all
 * related dashboard data. The deletion is audited (admin, IP, UA, timestamp)
 * via the backend `tutor_deletions` collection.
 */
export default function AdminTutorPermanentDelete({ applicationId, tutorName, onDeleted }) {
    const sm = useSuperMode();
    const [open, setOpen] = useState(false);
    const [phrase, setPhrase] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const phraseOk = phrase.trim() === "DELETE PERMANENTLY";

    const handleDelete = async () => {
        setBusy(true);
        try {
            await api.delete(`/admin/tutor-applications/${applicationId}/permanent`, {
                data: { confirm_phrase: "DELETE PERMANENTLY", reason: reason.trim() || undefined },
            });
            toast.success("Tutor profile deleted", {
                description: `${tutorName} has been permanently removed. All sessions have been revoked.`,
                duration: 6000,
            });
            setOpen(false);
            try { onDeleted && onDeleted(); } catch { /* ignore */ }
        } catch (err) {
            const status = err.response?.status;
            if (status === 403 && err.response?.data?.detail === "super_mode_required") {
                toast.error("Super Mode required", {
                    description: "Unlock Super Mode in the top bar before performing destructive actions.",
                });
            } else {
                toast.error("Could not delete tutor", {
                    description: formatApiError(err.response?.data?.detail) || err.message,
                });
            }
        } finally { setBusy(false); }
    };

    return (
        <>
            <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-4" data-testid="admin-tutor-delete-card">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-red-100 border border-red-200">
                        <ShieldAlert className="w-5 h-5 text-red-700" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-bold text-red-900">Danger Zone</h3>
                        <p className="text-xs text-red-800/80 mt-1 leading-relaxed">
                            Permanently delete this tutor profile, account, and all dashboard data. Active sessions will be revoked
                            immediately. The tutor can register again with the same email/phone — the new application
                            will go through the regular approval flow.
                        </p>
                        {!sm.active && sm.isSuperAdmin && (
                            <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 inline-flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Super Mode is locked — unlock it before deleting.
                            </div>
                        )}
                        <button
                            onClick={() => { setPhrase(""); setReason(""); setOpen(true); }}
                            disabled={!sm.isSuperAdmin}
                            className="inline-flex items-center gap-2 mt-3 px-3.5 py-1.5 text-xs font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="admin-tutor-delete-open-btn"
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Delete Permanently
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-[140] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150" data-testid="admin-tutor-delete-modal">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white px-5 py-4 flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6" />
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider opacity-80">Permanent Action</div>
                                <div className="text-base font-bold">Delete tutor profile</div>
                            </div>
                            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20" aria-label="Close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5 space-y-3 text-sm text-slate-700">
                            <p className="leading-relaxed">
                                Are you sure you want to permanently delete <strong className="text-red-700">{tutorName}</strong>?
                                All dashboard data, sessions, and KYC will be irreversibly wiped. <strong>This action cannot be undone.</strong>
                            </p>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
                                    Type <code className="px-1 py-0.5 bg-slate-100 rounded">DELETE PERMANENTLY</code> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={phrase}
                                    onChange={(e) => setPhrase(e.target.value)}
                                    placeholder="DELETE PERMANENTLY"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
                                    data-testid="admin-tutor-delete-phrase-input"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Reason (optional)</label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={2}
                                    placeholder="e.g. policy violation, requested by tutor over email"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
                                    data-testid="admin-tutor-delete-reason-input"
                                />
                            </div>
                        </div>
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button
                                onClick={() => setOpen(false)}
                                disabled={busy}
                                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-50"
                                data-testid="admin-tutor-delete-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={busy || !phraseOk}
                                className="px-3 py-1.5 text-sm font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                                data-testid="admin-tutor-delete-confirm-btn"
                            >
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
